// Maps the address bar to app state and back.

import type { SummaryLang } from '../../shared/api';
import { isSummaryLang } from '../../shared/lang';
import { parseYouTubeVideoId } from '../../shared/youtube-url';

export type Route =
  | {
      kind: 'home';
      /** The URL carried something (path, ?v=, shared text) that is not a YouTube link. */
      unrecognized?: string;
    }
  /** Without `lang` the summary language follows the user's preference. */
  | { kind: 'video'; videoId: string; lang?: SummaryLang }
  | { kind: 'reset-password'; token: string | null; error?: string };

export const RESET_PASSWORD_PATH = '/reset-password';

/** Query parameters that may carry a link: ours (`v`, `url`) and the PWA share target (`text`, `title`). */
const LINK_PARAMS = ['v', 'url', 'text', 'title'] as const;

export function parseLang(value: string | null | undefined): SummaryLang | undefined {
  const lang = value?.trim().toLowerCase();
  return isSummaryLang(lang) ? lang : undefined;
}

export function parseRoute(pathname: string, search: string): Route {
  const params = new URLSearchParams(search);
  const path = pathname.replace(/\/+$/, '') || '/';

  if (path === RESET_PASSWORD_PATH) {
    const error = params.get('error');
    return { kind: 'reset-password', token: params.get('token'), ...(error ? { error } : {}) };
  }

  const lang = parseLang(params.get('lang'));
  const withLang = (videoId: string): Route => ({ kind: 'video', videoId, ...(lang ? { lang } : {}) });

  for (const name of LINK_PARAMS) {
    const value = params.get(name);
    if (!value) continue;
    const id = parseYouTubeVideoId(value);
    if (id) return withLang(id);
  }

  // Path forms: /watch?v=ID, /shorts/ID, /ID, /https://www.youtube.com/watch?v=ID …
  // Browsers may collapse "//" and move "?v=" into our query string, so parse both together.
  if (path !== '/' || params.has('v')) {
    const id = parseYouTubeVideoId(pathname + search);
    if (id) return withLang(id);
  }

  const leftover =
    LINK_PARAMS.map((name) => params.get(name)).find((value) => value && value.trim()) ??
    (path !== '/' ? safeDecode(pathname.slice(1)) + search : undefined);
  return leftover ? { kind: 'home', unrecognized: leftover.trim() } : { kind: 'home' };
}

/** Canonical, shareable path of a summary: `/watch?v=ID` (+ `&lang=xx`). */
export function videoPath(videoId: string, lang?: SummaryLang): string {
  const params = new URLSearchParams({ v: videoId });
  if (lang) params.set('lang', lang);
  return `/watch?${params}`;
}

export function absoluteVideoUrl(videoId: string, lang?: SummaryLang): string {
  return new URL(videoPath(videoId, lang), window.location.origin).toString();
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
